const ISO_DATE_AT_START = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?/;
const ISO_DATETIME_ANYWHERE = /\b(\d{4})-(\d{2})-(\d{2})T(?=\d{2}:\d{2})/g;
const ISO_DATE_ANYWHERE = /\b(\d{4})-(\d{2})-(\d{2})(?=T|\s|\b)/g;
const SLASH_DATE_ANYWHERE = /\b(\d{2})\/(\d{2})\/(\d{4})\b/g;

const pad = (value) => String(value).padStart(2, "0");

export function formatDateDisplay(value, fallback = "—") {
  if (value === null || value === undefined || value === "") return fallback;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return fallback;
    return `${pad(value.getDate())}-${pad(value.getMonth() + 1)}-${value.getFullYear()}`;
  }

  const text = String(value).trim();
  const isoMatch = text.match(ISO_DATE_AT_START);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${day}-${month}-${year}`;
  }

  const slashMatch = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (slashMatch) return `${slashMatch[1]}-${slashMatch[2]}-${slashMatch[3]}`;
  if (/^\d{2}-\d{2}-\d{4}$/.test(text)) return text;

  return text;
}

export function formatDateTimeDisplay(value, fallback = "—", includeSeconds = true) {
  if (value === null || value === undefined || value === "") return fallback;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return fallback;
    const time = `${pad(value.getHours())}:${pad(value.getMinutes())}`;
    return `${formatDateDisplay(value, fallback)} ${time}${includeSeconds ? `:${pad(value.getSeconds())}` : ""}`;
  }

  const text = String(value).trim();
  const match = text.match(ISO_DATE_AT_START);
  if (!match) return formatDateDisplay(text, fallback);

  const [, year, month, day, hour, minute, second] = match;
  const date = `${day}-${month}-${year}`;
  if (!hour) return date;
  return `${date} ${hour}:${minute}${includeSeconds && second ? `:${second}` : ""}`;
}

export function getEvolutionEntryDate(value) {
  if (!value || typeof value !== "object" || value instanceof Date) return value;

  return (
    value.fechaMarcacionEntrada ??
    value.FechaMarcacionEntrada ??
    value.fechaEntrada ??
    value.FechaEntrada ??
    value.fechahora ??
    value.fechaHora ??
    value.FechaHora ??
    null
  );
}

function getEvolutionShiftSource(value) {
  if (!value || typeof value !== "object" || value instanceof Date) return value;

  const date = getEvolutionEntryDate(value);
  const time =
    value.horaMarcacionEntrada ??
    value.HoraMarcacionEntrada ??
    value.horaEntrada ??
    value.HoraEntrada;

  return date && time ? `${date} ${time}` : date;
}

export function getEvolutionShift(value, fallback = "Sin turno") {
  if (value === null || value === undefined || value === "") return fallback;

  if (typeof value === "object" && !(value instanceof Date) && value.turno) {
    return value.turno;
  }

  let hour;
  const source = getEvolutionShiftSource(value);
  if (source instanceof Date) {
    if (Number.isNaN(source.getTime())) return fallback;
    hour = source.getHours();
  } else {
    const text = String(source).trim();
    const match = text.match(/(?:T|\s)(\d{2}):\d{2}/);
    if (!match) return fallback;
    hour = Number(match[1]);
  }

  if (!Number.isInteger(hour) || hour < 0 || hour > 23) return fallback;
  return hour >= 7 && hour < 19 ? "Día" : "Noche";
}

export function formatEvolutionEntry(value, fallback = "Sin marcación") {
  const entryDate = getEvolutionEntryDate(value);
  if (!entryDate) return fallback;

  const date = formatDateDisplay(entryDate, fallback);
  const shift = getEvolutionShift(value);
  return shift === "Sin turno" ? date : `${date} - ${shift}`;
}

export function formatDateText(value) {
  if (value === null || value === undefined) return value;

  return String(value)
    .replace(ISO_DATETIME_ANYWHERE, (_match, year, month, day) => `${day}-${month}-${year} `)
    .replace(ISO_DATE_ANYWHERE, (_match, year, month, day) => `${day}-${month}-${year}`)
    .replace(SLASH_DATE_ANYWHERE, (_match, day, month, year) => `${day}-${month}-${year}`);
}

export function formatDateValuesForExport(value) {
  if (Array.isArray(value)) return value.map(formatDateValuesForExport);
  if (value instanceof Date) return formatDateTimeDisplay(value);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, formatDateValuesForExport(item)])
    );
  }
  return typeof value === "string" ? formatDateText(value) : value;
}

export function installSpreadsheetDateFormatting(spreadsheet) {
  const utils = spreadsheet?.utils;
  if (!utils || utils.__eethsaDateFormattingInstalled) return;

  ["json_to_sheet", "aoa_to_sheet"].forEach((methodName) => {
    const original = utils[methodName];
    if (typeof original !== "function") return;
    utils[methodName] = (data, ...args) => original(formatDateValuesForExport(data), ...args);
  });

  Object.defineProperty(utils, "__eethsaDateFormattingInstalled", {
    value: true,
    enumerable: false,
  });
}

export function installJsPdfDateFormatting(jsPDF) {
  const api = jsPDF?.API;
  if (!api || api.__eethsaDateFormattingInstalled || typeof api.text !== "function") return;

  const originalText = api.text;
  api.text = function formatPdfText(text, ...args) {
    const formatted = Array.isArray(text)
      ? text.map((line) => formatDateText(line))
      : formatDateText(text);
    return originalText.call(this, formatted, ...args);
  };

  Object.defineProperty(api, "__eethsaDateFormattingInstalled", {
    value: true,
    enumerable: false,
  });
}

function shouldSkipTextNode(node) {
  const parent = node.parentElement;
  if (!parent) return true;
  if (parent.closest("input, textarea, select, option, script, style, code, pre, [contenteditable='true'], .preserve-date-format")) {
    return true;
  }
  return false;
}

function formatTextNode(node) {
  if (shouldSkipTextNode(node)) return;
  const current = node.nodeValue;
  const formatted = formatDateText(current);
  if (formatted !== current) node.nodeValue = formatted;
}

function formatNodeTree(node) {
  if (!node) return;
  if (node.nodeType === Node.TEXT_NODE) {
    formatTextNode(node);
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;

  const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
  let textNode = walker.nextNode();
  while (textNode) {
    formatTextNode(textNode);
    textNode = walker.nextNode();
  }
}

export function installGlobalDateDisplayFormatter(root) {
  if (!root || typeof MutationObserver === "undefined") return () => {};

  formatNodeTree(root);
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === "characterData") {
        formatTextNode(mutation.target);
        return;
      }
      mutation.addedNodes.forEach(formatNodeTree);
    });
  });

  observer.observe(root, { childList: true, subtree: true, characterData: true });
  return () => observer.disconnect();
}
